<template>
  <nav role="navigation" aria-labelledby="rfp-rr-title">
    <ul class="paginate-links items paginate-ul">
      <li :class="{ disabled: firstPageSelected }">
        <a
          role="button"
          :aria-disabled="String(firstPageSelected)"
          :tabindex="firstPageSelected ? -1 : 0"
          @click="prevPage()"
          @keyup.enter="prevPage()"
          @keydown.space.prevent="prevPage()"
        >
          {{ prevText }}
        </a>
      </li>

      <li
        v-for="(page, index) in pages"
        :key="index"
        :class="{
          'number active paginate-li': page.selected,
          disabled: page.breakView,
        }"
      >
        <a v-if="page.breakView" role="presentation" tabindex="-1">…</a>
        <a
          v-else
          role="button"
          tabindex="0"
          :aria-current="isCurrentPage(page.index + 1) ? 'true' : undefined"
          @click="handlePageSelected(page.index + 1)"
          @keyup.enter="handlePageSelected(page.index + 1)"
          @keydown.space.prevent="handlePageSelected(page.index + 1)"
        >
          {{ page.content }}
        </a>
      </li>

      <li :class="{ disabled: lastPageSelected }">
        <a
          role="button"
          :aria-disabled="String(firstPageSelected)"
          :tabindex="lastPageSelected ? -1 : 0"
          @click="nextPage()"
          @keyup.enter="nextPage()"
          @keydown.space.prevent="nextPage()"
        >
          {{ nextText }}
        </a>
      </li>
    </ul>
  </nav>
</template>

<script>
export default {
  name: 'rfp-ratings-pagination-links',
  props: {
    value: {
      type: Number,
    },
    pageCount: {
      type: Number,
      required: true,
    },
    forcePage: {
      type: Number,
    },
    pageRange: {
      type: Number,
      default: 3,
    },
    marginPages: {
      type: Number,
      default: 1,
    },
    prevText: {
      type: String,
      default: 'Prev',
    },
    nextText: {
      type: String,
      default: 'Next',
    },
  },
  data() {
    return {
      innerValue: 1,
    };
  },
  computed: {
    selected: {
      get: function () {
        return this.value || this.innerValue;
      },
      /* istanbul ignore next */
      set: function (newValue) {
        /* istanbul ignore next */
        this.innerValue = newValue;
      },
    },
    pages: function () {
      let items = {};
      if (this.pageCount <= this.pageRange) {
        /* istanbul ignore next */
        for (let index = 0; index < this.pageCount; index++) {
          /* istanbul ignore next */
          let page = {
            index: index,
            content: index + 1,
            selected: index === this.selected - 1,
          };
          /* istanbul ignore next */
          items[index] = page;
        }
      } else {
        const halfPageRange = Math.floor(this.pageRange / 2);

        let setPageItem = (index) => {
          let page = {
            index: index,
            content: index + 1,
            selected: index === this.selected - 1,
          };

          items[index] = page;
        };

        let setBreakView = (index) => {
          let breakView = {
            breakView: true,
          };

          items[index] = breakView;
        };

        // 1st - loop thru low end of margin pages
        for (let i = 0; i < this.marginPages; i++) {
          setPageItem(i);
        }

        // 2nd - loop thru selected range
        let selectedRangeLow = 0;
        if (this.selected - halfPageRange > 0) {
          /* istanbul ignore next */
          selectedRangeLow = this.selected - 1 - halfPageRange;
        }

        let selectedRangeHigh = selectedRangeLow + this.pageRange - 1;
        if (selectedRangeHigh >= this.pageCount) {
          /* istanbul ignore next */
          selectedRangeHigh = this.pageCount - 1;
          /* istanbul ignore next */
          selectedRangeLow = selectedRangeHigh - this.pageRange + 1;
        }

        for (let i = selectedRangeLow; i <= selectedRangeHigh && i <= this.pageCount - 1; i++) {
          setPageItem(i);
        }

        // Check if there is breakView in the left of selected range
        if (selectedRangeLow > this.marginPages) {
          /* istanbul ignore next */
          setBreakView(selectedRangeLow - 1);
        }

        // Check if there is breakView in the right of selected range
        if (selectedRangeHigh + 1 < this.pageCount - this.marginPages) {
          setBreakView(selectedRangeHigh + 1);
        }

        // 3rd - loop thru high end of margin pages
        for (let i = this.pageCount - 1; i >= this.pageCount - this.marginPages; i--) {
          setPageItem(i);
        }
      }
      return items;
    },
    firstPageSelected() {
      return this.selected === 1;
    },
    lastPageSelected() {
      return this.selected === this.pageCount || this.pageCount === 0;
    },
  },
  beforeUpdate() {
    if (this.forcePage === undefined) return;
    /* istanbul ignore next */
    if (this.forcePage !== this.selected) {
      /* istanbul ignore next */
      this.selected = this.forcePage;
    }
  },
  methods: {
    handlePageSelected(selected) {
      if (this.selected === selected) return;

      this.innerValue = selected;
      this.$emit('input', selected);
    },
    prevPage() {
      if (this.selected <= 1) return;
      /* istanbul ignore next */
      this.handlePageSelected(this.selected - 1);
    },
    nextPage() {
      if (this.selected >= this.pageCount) return;

      this.handlePageSelected(this.selected + 1);
    },
    selectFirstPage() {
      if (this.selected <= 1) return;

      this.handlePageSelected(1);
    },
    selectLastPage() {
      if (this.selected >= this.pageCount) return;

      this.handlePageSelected(this.pageCount);
    },
    resetPaginationLink() {
      this.innerValue = 1;
    },
    isCurrentPage(pageNumber) {
      return this.selected === pageNumber;
    },
  },
};
</script>
